<template>
    <div class="s-form-login">
        <div
            v-if="content.title"
            class="title"
            :class="{ 'title-accent': content.hasTitleAccent }"
            v-html="content.title"
        />
        <div v-if="content.subtitles" class="subtitle">
            <p v-for="(item, i) in content.subtitles" :key="`subtitles-${i}`" v-html="item" />
        </div>
        <el-form ref="form" :model="form" :rules="rules">
            <a-row>
                <el-form-item prop="email">
                    <el-input
                        ref="email"
                        v-model="form.email"
                        autofocus
                        name="email"
                        type="input"
                        :label="formLabelText({ name: 'email' })"
                        @input="onInputField"
                    />
                </el-form-item>
            </a-row>
            <a-row>
                <el-form-item prop="password">
                    <el-input
                        ref="password"
                        v-model="form.password"
                        type="password"
                        name="password"
                        :label="formLabelText({ name: 'password' })"
                        show-password-icon
                        @input="onInputField"
                    />
                </el-form-item>
            </a-row>
            <el-form-error v-if="errorMessage" size="large" show-icon>
                <span v-html="errorMessage" />
            </el-form-error>
            <p v-if="content.note" class="note" v-html="content.note" />
            <a-button
                v-if="content.button"
                v-bind="content.button"
                :event="{ doNotSendGA: true }"
                @click="onSubmitForm"
            />
        </el-form>
        <template v-if="content.extraButtons?.length">
            <a-button
                v-for="(button, i) in content.extraButtons"
                :key="`form-login-extra-button-${i}`"
                class="extra-button"
                v-bind="button"
                :event="button.event || { doNotSendGA: true }"
                @click="clickExtraButton(button)"
            />
        </template>
        <div v-if="content.info" class="info">
            <p v-for="(item, i) in content.info" :key="`info-${i}`" v-html="item" />
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import AButton from '@core/components/button/button.vue';
import ElFormError from '@core/components/form/form-error.vue';
import ElFormItem from '@core/components/form/form-item.vue';
import ElForm from '@core/components/form/form.vue';
import ElInput from '@core/components/input/input.vue';
import ARow from '@core/components/row/row.vue';
import form from '@core/mixins/form';
import * as twoFa from '../../two-fa.ts';

export default {
    name: 'TrialFormLogin',

    components: {
        AButton,
        ElForm,
        ElFormError,
        ElFormItem,
        ElInput,
        ARow,
    },

    mixins: [form],

    props: {
        content: {
            type: Object,
            default: null,
        },

        formStep: {
            type: Number,
            default: null,
        },
    },

    emits: ['error', 'loading', 'submit'],

    data: () => ({
        form: {
            email: '',
            password: '',
        },
        errorMessage: '',
    }),

    computed: {
        rules() {
            return {
                email: [
                    {
                        trigger: 'submit',
                        validator: this.validateEmail,
                        message: this.formErrorText({ name: 'email' }),
                    },
                ],
                password: [
                    {
                        trigger: 'submit',
                        validator: this.validatePassword,
                        message: this.formErrorText({ name: 'password' }),
                    },
                ],
            };
        },
    },

    methods: {
        ...mapActions({
            accountLogin: 'trial/accountLogin',
            logout: 'user/logout',
        }),

        onInputField() {
            this.errorMessage = '';
            this.$nextTick(() => this.$refs.form?.clearValidate?.());
        },

        clickExtraButton(button) {
            if (!button.resetFields) return;

            this.errorMessage = '';

            this.$nextTick(() => {
                this.$refs.form.resetFields();
                this.$refs.form.clearValidate();
            });
        },

        async onSubmitForm() {
            try {
                this.prepareFormToValidate();
                await this.$refs.form.validate();
            } catch (e) {
                this.setFocusErrorField();
                return;
            }

            try {
                this.$emit('loading', true);

                // The login endpoint always issues a fresh pre-session, even when the user is already
                // signed in. For 2FA-enabled accounts that means the redirect to the account domain
                // would land on its profile page instead of returning to our callback. Force a logout
                // first so the redirect actually completes the challenge.
                if (this.accountData) {
                    await this.logout();
                }

                const response = await this.accountLogin({
                    email: this.form.email,
                    password: this.form.password,
                });

                if (twoFa.requires2FASetupFirst(response)) {
                    this.errorMessage = twoFa.setupFirstMessage(this.formElementsDefault?.errors?.apiErrorCodes?.two_fa_setup_required);
                    this.$emit('loading', false);
                    return;
                }

                if (twoFa.requires2FA(response)) {
                    twoFa.redirectTo2FA(response, {
                        activeForm: 'login',
                        formStep: this.formStep,
                        userData: { email: this.form.email },
                    });
                    return;
                }

                if (twoFa.isOtpLocked(response)) {
                    this.errorMessage = this.formElementsDefault?.errors?.apiErrorCodes?.otp_locked ||
                        'Too many attempts. Please try again later.';
                    this.setFocus('password');
                    this.$emit('loading', false);
                    return;
                }

                if (response.status !== 200) {
                    const msg = 'Wrong email or password';
                    this.errorMessage = this.content?.messages?.error || msg;
                    this.setFocus('password');
                    this.$emit('loading', false);
                    return;
                }

                this.$emit('submit');
            } catch (e) {
                this.$emit('error');
            }
        },
    },
};
</script>

<style lang="pcss" scoped>
.s-form-login {
    display: flex;
    position: relative;
    flex-direction: column;
    padding: 40px 16px 56px;
    justify-content: center;
    background: var(--av-inversed-primary);

    @media (--viewport-mobile-wide) {
        border-radius: 4px;
        box-shadow: var(--av-shadow-small);
        border: 1px solid var(--av-brand-light);
    }

    @media (--viewport-tablet) {
        padding: 40px 48px;
    }

    @media (--viewport-desktop) {
        width: 100%;
        margin: 0 auto;
    }

    .title {
        @mixin title-accent;
        text-align: center;
        color: var(--av-nav-primary);
    }

    .title-accent {
        @mixin display-accent;
    }

    .subtitle {
        @mixin paragraph;
        margin: 8px 0 0;
        text-align: center;

        :deep(a) {
            cursor: pointer;
            text-decoration: none;
            color: var(--av-brand-secondary);

            &:hover {
                color: var(--av-brand-secondary);
            }
        }
    }

    .note {
        text-align: end;
        margin-bottom: 16px;

        :deep(.a-link__content) {
            @mixin descriptor;
            color: var(--av-brand-secondary);
        }
    }

    .info {
        @mixin body;
        margin-top: 24px;
        text-align: center;
        color: var(--av-fixed-secondary);

        a {
            color: var(--av-brand-primary);
        }
    }

    .extra-button {
        margin: 16px 0 0;

        &.a-button_type_inverse {
            border-radius: 4px;
            border: 1px solid var(--av-brand-primary);

            &:hover {
                background: var(--av-brand-accent);
            }

            &:active {
                background: var(--el-secondary-active);
            }

            :deep(.a-button__content) {
                color: var(--av-brand-primary);
                @mixin paragraph-accent;
            }
        }
    }

    :deep(.el-form) {
        width: 100%;
        margin: 32px 0 0;
        text-align: start;

        @media (--viewport-tablet) {
            margin: 24px 0 0;
        }

        .el-input__label {
            &.is-active {
                font-weight: 700;
            }
        }

        .el-form-item__error {
            @mixin caption;
            padding-bottom: 16px;
        }
    }

    :deep(.a-button) {
        width: 100%;
    }
}
</style>
